import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Row, Col, Form, Input, Checkbox } from 'antd';
import { FormInstance } from 'antd/lib/form/Form';

import { IContact } from '../../common/model/mailchimp/contact.model';
import { getInterests } from '../../common/model/mailchimp/enums/mailchimpInterest.enum';
import useI18n from '../../common/hooks/useI18n';

import { AutocompleteAddressField, FileInputButton } from '.';

type Props = {
  form: FormInstance;
  onFinish: (contact: IContact) => void;
  showAddress?: boolean;
  showSubject?: boolean;
  showInterests?: boolean;
  showCv?: boolean;
};

const InputContainer = styled.div`
  color: ${(props) => props.theme.colors.secondary};
`;

const FileName = styled.span`
  margin-left: 16px;
`;

const ContactForm: React.FC<Props> = ({
  form,
  onFinish,
  showAddress,
  showSubject,
  showInterests,
  showCv,
}) => {
  const i18n = useI18n();

  const [isMounted, setIsMounted] = useState(false);
  const [formData, setFormData] = useState<FormData>(null);
  const [formDataEntryValue, setFormDataEntryValue] = useState<
    FormDataEntryValue
  >(null);

  const privacyPolicyAcceptedText = useMemo(
    () => i18n.t('contactForm.privacyPolicyAccepted'),
    [i18n]
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const onFileInputButtonChange = (changedFormData: FormData) => {
    if (changedFormData) {
      for (const entry of changedFormData.entries()) {
        setFormDataEntryValue(entry[1]);
        setFormData(changedFormData);
        return;
      }
    }
    setFormDataEntryValue(null);
    setFormData(null);
  };

  const validateMessages = {
    required: i18n.t('contactForm.validateRequired'),
    types: {
      email: i18n.t('contactForm.validateEmail'),
    },
  };

  const subjectLabel = `contactForm.${showCv ? 'subjectCv' : 'subject'}`;

  return (
    <Form
      validateMessages={validateMessages}
      form={form}
      onFinish={(values) => {
        const contact: IContact = {
          EMAIL: values.email,
          FNAME: values.name,
          LNAME: values.lastName,
          interests: values.interests || [],
        };
        if (values.phone) {
          contact.PHONE = values.phone;
        }
        if (values.address) {
          contact.HADDRESS = values.address;
        }
        if (values.subject) {
          contact.SUBJECT = values.subject;
        }
        if (formData) {
          contact.formData = formData;
        }
        onFinish(contact);
      }}
    >
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <InputContainer>
            <Form.Item
              labelCol={{ span: 24 }}
              name="name"
              label={i18n.t('contactForm.name')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </InputContainer>
        </Col>
        <Col xs={24} lg={12}>
          <InputContainer>
            <Form.Item
              labelCol={{ span: 24 }}
              name="lastName"
              label={i18n.t('contactForm.lastName')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </InputContainer>
        </Col>
        <Col xs={24} lg={12}>
          <InputContainer>
            <Form.Item
              labelCol={{ span: 24 }}
              name="email"
              label={i18n.t('contactForm.email')}
              rules={[{ required: true, type: 'email' }]}
            >
              <Input />
            </Form.Item>
          </InputContainer>
        </Col>
        <Col xs={24} lg={12}>
          <InputContainer>
            <Form.Item
              labelCol={{ span: 24 }}
              name="phone"
              label={i18n.t('contactForm.phone')}
            >
              <Input />
            </Form.Item>
          </InputContainer>
        </Col>
        {typeof google !== 'undefined' &&
        typeof google.maps.Map !== 'undefined' &&
        isMounted &&
        showAddress ? (
          <AutocompleteAddressField />
        ) : null}
        {showSubject && (
          <Col span={24}>
            <InputContainer>
              <Form.Item
                labelCol={{ span: 24 }}
                name="subject"
                label={i18n.t(subjectLabel)}
                rules={[{ required: true }]}
              >
                <Input.TextArea rows={5} />
              </Form.Item>
            </InputContainer>
          </Col>
        )}
        {showInterests && (
          <Col span={24}>
            <InputContainer>
              <Form.Item
                labelCol={{ span: 24 }}
                name="interests"
                label={i18n.t('contactForm.interests')}
              >
                <Checkbox.Group
                  options={getInterests().map((i) => ({
                    label: i18n.t(`enums.mailchimpInterest.${i}`),
                    value: i,
                  }))}
                />
              </Form.Item>
            </InputContainer>
          </Col>
        )}
        {showCv && (
          <Col span={24}>
            <Form.Item>
              <FileInputButton
                label={i18n.t('contactForm.cv')}
                uploadFileName="FILE"
                onChange={onFileInputButtonChange}
              />
              {formData && (
                <FileName>{(formDataEntryValue as File).name}</FileName>
              )}
            </Form.Item>
          </Col>
        )}
        <Col span={24}>
          <InputContainer>
            <Form.Item
              labelCol={{ span: 24 }}
              name="privacyPolicyAccepted"
              valuePropName="checked"
              initialValue={false}
              rules={[
                {
                  required: true,
                  transform: (value) => value || undefined, // https://github.com/ant-design/ant-design/issues/8261#issuecomment-439833733
                  type: 'boolean',
                  message: i18n.t('contactForm.validatePrivacyPolicy'),
                },
              ]}
            >
              <Checkbox>
                <span
                  key={
                    privacyPolicyAcceptedText.substring(0, 10) +
                    new Date().getTime()
                  }
                  dangerouslySetInnerHTML={{
                    __html: privacyPolicyAcceptedText,
                  }}
                />
              </Checkbox>
            </Form.Item>
          </InputContainer>
        </Col>
      </Row>
    </Form>
  );
};

export default ContactForm;
